import { APIProvider, Map } from "@vis.gl/react-google-maps";
import type { ReactNode } from "react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";

interface GoogleMapWrapperProps {
  defaultCenter?: google.maps.LatLngLiteral;
  defaultZoom?: number;
  mapTypeId?: string;
  children?: ReactNode;
  className?: string;
  fullscreenControl?: boolean;
  disableDefaultUI?: boolean;
  onClick?: (e: {
    detail: { latLng: google.maps.LatLngLiteral | null };
  }) => void;
}

export function GoogleMapWrapper({
  defaultCenter = { lat: 52.1326, lng: 5.2913 },
  defaultZoom = 7,
  mapTypeId,
  children,
  className,
  fullscreenControl,
  disableDefaultUI,
  onClick,
}: GoogleMapWrapperProps) {
  const { resolvedTheme } = useTheme();
  const { data: config, isLoading } = trpc.clientConfig.useQuery(undefined, {
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const apiKey = config?.googleMapsApiKey ?? "";
  const mapId = config?.googleMapId ?? "";

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-muted animate-pulse",
          className,
        )}
      >
        <p className="text-muted-foreground text-sm">Loading map...</p>
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div
        className={cn("flex items-center justify-center bg-muted", className)}
      >
        <p className="text-muted-foreground text-sm">
          Google Maps API key not configured. Set GOOGLE_MAPS_API_KEY or
          GOOGLE_PLACES_API_KEY in your .env file.
        </p>
      </div>
    );
  }

  return (
    <APIProvider apiKey={apiKey} libraries={["places", "marker"]}>
      <Map
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        mapId={mapId}
        colorScheme={resolvedTheme === "dark" ? "DARK" : "LIGHT"}
        className={cn("w-full h-full", className)}
        {...(mapTypeId ? { mapTypeId } : {})}
        gestureHandling="greedy"
        tilt={0}
        disableDefaultUI={disableDefaultUI ?? false}
        fullscreenControl={fullscreenControl}
        onClick={onClick}
      >
        {children}
      </Map>
    </APIProvider>
  );
}
